import { action, computed, observable } from 'mobx';
import { Family, INewFamily } from '../_interfaces/Family';
import { IPublicUser } from '../_interfaces/IUser';
import { FamilyService } from '../_services/FamilyService';
import { validateEmail } from '../util/validateEmail';
import { AStoreWithMappedData } from './AStoreWithMappedData';
import { SpecialFilterFamilyID } from './CollectionFilterStore';

export const MAX_AMOUNT_OF_INVITES_FOR_FAMILY = 15;

export class FamilyStore extends AStoreWithMappedData<Family> {
    private _familyService: FamilyService;
    private _familyIDToRemove?: number;
    @observable
    public _families: Array<Family> = [];
    @observable
    public _lastClickedFamilyID: number | undefined;

    @computed
    public get families(): Array<Family> {
        return this._families;
    }

    @computed
    public get lastClickedFamilyID(): number | undefined {
        return this._lastClickedFamilyID;
    }

    @action
    public reloadData(): void {
        this.loadData();
    }

    public getFamilyById(id: number): Family {
        if (this.recordsMap.get(id) === undefined) {
            throw Error('Family was not found');
        } else {
            return this.recordsMap.get(id)!;
        }
    }

    public deleteFamily(familyID: number, currentUserID: number): boolean {
        if (confirm('Are you sure you want to delete this family? This cannot be undone!')) {
            const familyToRemove = this.recordsMap.get(familyID);

            //Should be done on server?
            if (familyToRemove) {
                familyToRemove.user_ids.forEach(userID => {
                    this._familyService.removeUser(userID, familyID);
                });

                this._familyService.removeUser(currentUserID, familyID);
                this._families.splice(this._families.findIndex(family => family.id === familyToRemove.id), 1);
            }

            return true;
        } else {
            return false;
        }
    }

    public leaveFamily(currentUserID: number, familyID: number): boolean {
        if (confirm('Are you sure you want to leave this family? You will have to be reinvited to see their shared collections')) {
            this._familyService.removeUser(currentUserID, familyID).then(_ => {
                this._familyIDToRemove = familyID;
                this._families = this.families.filter(family => {
                    return family.id !== familyID;
                });
            });
            return true;
        } else {
            return false;
        }
    }

    public removeUserFromFamily(familyID: number, user: IPublicUser): boolean {
        if (confirm('Are you sure you want to remove ' + user.first_name + ' ' + user.last_name + ' from this family?')) {
            this._familyService.removeUser(user.id, familyID).then(_ => {
                this.loadData();
            });

            return true;
        } else {
            return false;
        }
    }

    public async getCollectionsForFamily(family_id: number): Promise<Array<number>> {
        return this._familyService.getCollectionIdsByFamilyId(family_id);
    }

    public async acceptInvite(invite_key: string): Promise<void> {
        return this._familyService.acceptInvite(invite_key);
    }

    public constructor(authToken: string) {
        super();
        this._familyService = new FamilyService(authToken);
        this.loadData();
    }

    protected loadData(): Promise<void> {
        return this._familyService.getAll().then((families: Array<Family>) => {
            this._families = families;
            this.addRecordsToMap(families.filter(family => family.id !== this._familyIDToRemove));
        });
    }

    public createNewFamily(name: string): Promise<number> {
        console.assert(name.trim() !== '', 'A blank name was passed in while creating a family @FamilyStore');

        const newFamily: INewFamily = {
            name
        };

        return this._familyService.create(newFamily).then(family_id => {
            this.loadData();
            return family_id;
        });
    }

    public sendFamilyInvite(email: string, family_id: number): Promise<void> {
        console.assert(validateEmail(email), `invalid email "${email}" was passed into sendFamilyInvite @FamilyStore`);

        return this._familyService.sendInvite(email, family_id);
    }

    public addFamilyToCollection(collectionId: number, familyId: number): Promise<void> {
        return this._familyService.addFamilyToCollection(collectionId, familyId).then(() => {
            this.loadData();
        });
    }

    public removeFamilyFromCollection(collectionId: number, familyId: number): void {
        this._familyService.removeFamilyFromCollection(collectionId, familyId).then(data => {
            this.loadData();
        });
    }

    public setLastClickedFamily(id: number | undefined): void {
        this._lastClickedFamilyID = id;
    }

    public isSpecialFilterFamilyID(id: number): boolean {
        if (id === SpecialFilterFamilyID.ALL || id === SpecialFilterFamilyID.MY_STUFF) {
            return true;
        }
        console.assert(id >= 0, 'a negative id was passed without a special case in SpecialFilterFamilyID');
        return false;
    }

    public static GetFamilyDisplayName(name: string): string {
        return name + ' Family';
    }
}



// WEBPACK FOOTER //
// ./src/_stores/FamilyStore.ts